// The shared "spinning goat" lifecycle: shows the layout's #loading-overlay
// while a SLOW screen load is in flight, whatever started it (#13805).
//
// The overlay ships visible in each page's HTML and is hidden once that page
// renders — but nothing re-showed it when the user navigated away, so a slow
// server response left the user staring at a frozen page. This module re-shows
// the CURRENT page's overlay shortly after a screen load starts; once the new
// page renders (Turbo) or replaces the document (native navigation), the fresh
// copy is hidden by the next page's own boot.
//
// Loads it observes (see output INVENTORY for the audit behind this list):
//   • Turbo Drive visits            turbo:visit
//   • Turbo form submissions        turbo:submit-start / turbo:submit-end
//   • native forms, data-turbo=false   the `submit` event (a real click or
//                                       requestSubmit(); invalid forms fire none)
//   • native links, data-turbo=false   the `click` event
//   • programmatic navigations      beginNavigation(), for code that calls
//                                    form.submit() / location.assign() — those
//                                    fire NO DOM event, so they must say so
//                                    (impersonate.ts does).
// Never observed on purpose: Turbo Frames (a region, not a screen), background
// fetches, new-tab / modifier clicks, downloads, hash and mailto links.
//
// The reveal is delayed so fast pages never flash the overlay (same idea as
// Turbo's own progress-bar delay).
export const SLOW_VISIT_OVERLAY_DELAY_MS = 300

// `data-goat-loader="off"` on a form, a link or an ancestor opts a native
// navigation out — the one hand-hold for a response the browser turns into a
// download (Content-Disposition is invisible before the click).
export const LOADER_OPT_OUT_SELECTOR = '[data-goat-loader="off"]'

// URLs the click filter treats as downloads even without a `download`
// attribute: an explicit attachment disposition or a document extension.
const ATTACHMENT_QUERY = "attachment"
const ATTACHMENT_PATH = /\.(csv|pdf|xlsx?|docx?|pptx?|zip)$/i

// After a redirected Turbo form submission the follow-up visit normally starts
// within a tick (Turbo proposes it once it has read the redirect body). If it
// never starts — a `turbo:before-visit` guard canceled it, or Turbo gave up —
// nothing would ever settle the overlay, so the wait is bounded.
export const REDIRECT_VISIT_GRACE_MS = 5000

let timer = null
// What is loading: "visit" (Turbo visit), "submit" (Turbo form POST), "native"
// (browser navigation the document will not survive) or null. Non-null means a
// screen load is in flight and a holder release must NOT hide the overlay.
let pending = null
// Page-level holders (an in-flight AJAX call that borrowed the overlay via
// acquireLoadingOverlay). While a load is in flight, a release must NOT hide
// the overlay: the user has navigated away and the overlay now belongs to the
// slow load, whose own settle() will hide it when the new page renders.
let holders = 0
// Every render starts a new page generation. A release handed out by an
// earlier generation (the user Turbo-navigated away from the borrowing page
// before its request answered) is a no-op, so it can never drop a holder the
// CURRENT page owns. That stale release does fire today whenever the user
// leaves lead_orders/new mid-quote; it only has anything to steal once the
// page it lands on holds the overlay itself (lead_orders/new reloads on entry,
// so today that is a page other than the one that minted it).
let generation = 0
// Set by a redirected `turbo:submit-end` until the follow-up `turbo:visit`
// arrives: the one window in which a canceled visit would strand the overlay.
let redirectVisitTimer = null

// Every show/hide announces the overlay's resulting visibility on `document`
// for the OTHER bundle (#14336): the React DetailPanel
// (app/frontend/react/lib/pageLoadingIndicator.ts) defers its own loading goat
// while this one covers the viewport. The two bundles cannot share a module
// instance, so the event is the bridge; the name is duplicated there
// verbatim and its spec pins the two copies together. The detail reports the
// element's actual class state, so a listener never learns a "show" that a
// layout without the element could not paint.
export const LOADING_OVERLAY_VISIBILITY_EVENT = "goat:loading-overlay-visibility"

const overlay = () => document.getElementById("loading-overlay")

const announceVisibility = (element) => {
  const visible = !element.classList.contains("hidden")
  document.dispatchEvent(new CustomEvent(LOADING_OVERLAY_VISIBILITY_EVENT, { detail: { visible } }))
}

const show = () => {
  const element = overlay()
  if (!element) return
  element.classList.remove("hidden")
  announceVisibility(element)
}

const hide = () => {
  const element = overlay()
  if (!element) return
  element.classList.add("hidden")
  announceVisibility(element)
}

const cancelTimer = () => {
  if (timer) clearTimeout(timer)
  timer = null
}

const cancelRedirectWatch = () => {
  if (redirectVisitTimer) clearTimeout(redirectVisitTimer)
  redirectVisitTimer = null
}

// A screen load started: reveal after the no-flash delay. A second start
// before the first settles restarts the delay (the newest load is the one the
// user is waiting for).
const begin = (kind) => {
  pending = kind
  cancelRedirectWatch()
  cancelTimer()
  timer = setTimeout(show, SLOW_VISIT_OVERLAY_DELAY_MS)
}

// The page body was replaced (or restored): everything this page borrowed died
// with it, and any release it still holds is now stale.
const settle = () => {
  pending = null
  holders = 0
  generation += 1
  cancelRedirectWatch()
  cancelTimer()
  hide()
}

// A screen load that will NOT replace the page after all (canceled, stopped,
// dismissed). Holders keep the overlay if they still own it.
const cancelPending = () => {
  pending = null
  cancelRedirectWatch()
  cancelTimer()
  if (holders === 0) hide()
}

// Borrow the shared overlay from page code (e.g. an inline script showing it
// around an AJAX call). Returns the matching release; call it exactly once.
// The overlay hides when the last holder of the CURRENT generation releases
// and no screen load is in flight. A release from a previous generation (the
// page that minted it has been replaced) does nothing.
export function acquireLoadingOverlay() {
  const mintedIn = generation
  let released = false
  holders += 1
  show()
  const release = () => {
    if (released) return
    released = true
    if (mintedIn !== generation) return
    holders = Math.max(0, holders - 1)
    if (holders === 0 && pending === null) hide()
  }
  // A release that can no longer affect anything: already called, or minted by
  // a page generation that has since been replaced. Lets a queue skip it.
  Object.defineProperty(release, "stale", {
    get: () => released || mintedIn !== generation
  })
  return release
}

// For page code that pairs one show with one hide PER REQUEST (an inline
// script wrapping several AJAX calls that may overlap): show() borrows a
// holder, hide() releases one outstanding borrow, so the overlay stays up
// until as many hides as shows have happened. Releases are interchangeable
// counters — the queue tracks HOW MANY borrows are outstanding, not which
// request each belongs to, so response order is irrelevant. A hide with
// nothing outstanding is a no-op. A borrow that outlives its page is dropped
// by acquireLoadingOverlay's generation guard, and hide() skips past such stale
// entries — so a queue kept alive across a Turbo render (no hard reload) still
// releases a CURRENT borrow on every hide instead of spending it on a no-op.
export function createLoadingOverlayQueue() {
  const releases = []
  const live = () => releases.filter((release) => !release.stale)
  return {
    show() {
      releases.push(acquireLoadingOverlay())
    },
    hide() {
      while (releases.length > 0) {
        const release = releases.shift()
        if (release.stale) continue
        release()
        return
      }
    },
    get outstanding() {
      return live().length
    }
  }
}

// A screen load the DOM cannot see is starting: form.submit() (which fires no
// submit event), location.assign() / location.href. Call it right before the
// navigation; the overlay shows after the delay exactly like a Turbo visit.
// `source` names the caller for readers of the call site; it changes nothing.
// eslint-disable-next-line no-unused-vars
export function beginNavigation(source = "programmatic") {
  begin("native")
}

// The navigation announced with beginNavigation() (or seen on a native form /
// link) is not happening after all.
export function settleNavigation() {
  if (pending !== null) cancelPending()
}

const optedOut = (element) => element.closest(LOADER_OPT_OUT_SELECTOR) !== null

// Turbo Drive is on for the whole app; a form or link is native only when it —
// or an ancestor — says data-turbo="false" (the convention every native
// surface in app/views uses). A nearer data-turbo="true" wins, as in Turbo.
const drivenNatively = (element) => element.closest("[data-turbo]")?.getAttribute("data-turbo") === "false"

// Only a navigation of THIS window replaces this document.
const targetsThisWindow = (name) => {
  const target = (name || "").trim().toLowerCase()
  return target === "" || target === "_self"
}

const onNativeSubmit = (event) => {
  const form = event.target
  if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return
  if (!drivenNatively(form) || optedOut(form)) return
  const target = event.submitter?.getAttribute("formtarget") ?? form.getAttribute("target")
  if (!targetsThisWindow(target)) return
  begin("native")
}

const onNativeClick = (event) => {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const link = event.target instanceof Element ? event.target.closest("a[href]") : null
  if (!link || !drivenNatively(link) || optedOut(link)) return
  if (link.hasAttribute("download") || !targetsThisWindow(link.getAttribute("target"))) return
  let url
  try {
    url = new URL(link.href, window.location.href)
  } catch {
    return
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return
  if (url.origin !== window.location.origin) return
  // Same-document hash change: the page stays.
  if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return
  if (url.searchParams.get("disposition") === ATTACHMENT_QUERY || ATTACHMENT_PATH.test(url.pathname)) return
  begin("native")
}

// A form submission Turbo drives itself. A frame-targeted form is a region
// load (the frame shows its own busy state), not a screen load.
const turboFormTargetsFrame = (form) => {
  if (!form) return false
  const frame = form.getAttribute("data-turbo-frame")
  if (frame) return frame !== "_top"
  return form.closest("turbo-frame") !== null
}

const onTurboSubmitStart = (event) => {
  const form = event.detail?.formSubmission?.formElement
  if (turboFormTargetsFrame(form) || (form && optedOut(form))) return
  begin("submit")
}

// success + redirect: Turbo now visits the redirect target, and that visit's
// render settles the overlay — unless the visit never starts (a
// `turbo:before-visit` guard canceled it), which the bounded watch and the
// before-visit check below catch. Anything else (422 re-render, a Turbo Stream
// response, a network error) renders no new page — settle now.
const onTurboSubmitEnd = (event) => {
  if (pending !== "submit") return
  const detail = event.detail ?? {}
  if (detail.success && detail.fetchResponse?.redirected) {
    if (redirectLeavesOrigin(detail.fetchResponse)) {
      // Turbo cannot visit another origin: it assigns window.location and the
      // browser replaces the document. No Turbo visit will follow, and no
      // watch should cut the goat short while that page loads.
      pending = "native"
      return
    }
    pending = "visit"
    cancelRedirectWatch()
    redirectVisitTimer = setTimeout(cancelPending, REDIRECT_VISIT_GRACE_MS)
    return
  }
  settle()
}

const redirectLeavesOrigin = (fetchResponse) => {
  const location = fetchResponse.location
  if (!location) return false
  try {
    return new URL(location, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

// The follow-up visit of a redirected submission was proposed and a guard
// canceled it (the unsaved-reorder prompt does exactly this): the page stays,
// so the overlay must not. Read defaultPrevented after dispatch completes —
// guards register later than this module and run after it. A canceled visit
// while some OTHER visit is already in flight changes nothing: that visit
// still renders and settles.
const onTurboBeforeVisit = (event) => {
  if (!redirectVisitTimer) return
  setTimeout(() => {
    if (event.defaultPrevented && redirectVisitTimer) cancelPending()
  }, 0)
}

// A page restored from the back/forward cache comes back with whatever DOM it
// had — possibly a goat it was showing for the navigation the user just backed
// out of. The restored page is interactive: nothing may cover it.
const onPageShow = () => settle()

// A dirty-form guard raised a "Leave site?" prompt. If the user cancels, the
// page stays and no event says so; if they leave, the load proceeds without
// us. Drop the pending native loader once the prompt was raised — never a
// stuck goat over a page the user chose to keep.
const onBeforeUnload = (event) => {
  if (pending !== "native") return
  setTimeout(() => {
    if (event.defaultPrevented) cancelPending()
  }, 0)
}

// Escape is the browser's stop key: a native load the user stopped leaves the
// old page in place, so the goat goes with it.
const onKeyDown = (event) => {
  if (event.key === "Escape" && pending === "native") cancelPending()
}

export function initSlowVisitOverlay() {
  // Boot starts from a clean slate (also what lets each test re-init).
  pending = null
  holders = 0
  generation += 1
  cancelRedirectWatch()
  cancelTimer()
  // turbo:visit (not turbo:before-visit) — it fires only once the visit
  // actually proceeds, so a canceled navigation can never strand the overlay.
  // Restoration visits render from cache before the delay elapses, so the
  // back button never flashes it.
  document.addEventListener("turbo:visit", () => begin("visit"))
  document.addEventListener("turbo:before-visit", onTurboBeforeVisit)
  document.addEventListener("turbo:render", settle)
  document.addEventListener("turbo:load", settle)
  // A failed fetch means no render will ever come — never leave the overlay
  // stuck over a page the user can still interact with (#6506 bug class).
  document.addEventListener("turbo:fetch-request-error", settle)
  // Turbo form POSTs: the slow part is the POST itself, before any visit.
  document.addEventListener("turbo:submit-start", onTurboSubmitStart)
  document.addEventListener("turbo:submit-end", onTurboSubmitEnd)
  // Native navigations Turbo does not drive (#13805).
  document.addEventListener("submit", onNativeSubmit)
  document.addEventListener("click", onNativeClick)
  document.addEventListener("keydown", onKeyDown)
  window.addEventListener("pageshow", onPageShow)
  window.addEventListener("beforeunload", onBeforeUnload)
}
